/* Thin Graph client. No SDK — the surface we need is small, and @microsoft/
   microsoft-graph-client would be a large dependency for `fetch` plus a token.

   Token acquisition is injected rather than imported so this file has no
   dependency on MSAL, which keeps it testable and keeps the offline path honest:
   nothing here is reachable without a token, which is exactly why the offline
   read path goes to IndexedDB and never through this class (§7 rule 3). */

const GRAPH = 'https://graph.microsoft.com/v1.0';

export type TokenProvider = (scopes: string[]) => Promise<string>;

export class GraphError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'GraphError';
  }
}

export interface ListItemFields {
  [key: string]: unknown;
}

interface GraphListItem {
  id: string;
  fields: ListItemFields;
}

interface GraphDriveItem {
  id: string;
  webUrl: string;
}

/** Graph refuses a simple upload over 4MB. Stated as bytes because the limit is
 *  on the request, not on anybody's idea of a megabyte. */
const SIMPLE_UPLOAD_MAX = 4 * 1024 * 1024;

/** Chunk size for a resumable upload. Must be a multiple of 320 KiB — Graph
 *  rejects anything else — and bigger means fewer round trips. */
const CHUNK = 320 * 1024 * 10;

interface GraphCollection<T> {
  value: T[];
  '@odata.nextLink'?: string;
}

export class GraphClient {
  constructor(
    private readonly getToken: TokenProvider,
    private readonly siteId: string,
    private readonly scopes: string[] = ['Sites.ReadWrite.All'],
  ) {}

  private async request<T>(url: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken(this.scopes);
    const res = await fetch(url.startsWith('http') ? url : `${GRAPH}${url}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });

    if (!res.ok) {
      const body = await res.json().catch(() => null);
      /* Graph puts the only useful part in error.message — which column it did
         not like, which value it could not parse. The status and the URL name
         the row you were editing and say nothing about why it was refused, so a
         400 read "Graph 400 on .../items/7/fields" and sent you looking at the
         item instead of the field. */
      const detail = (body as { error?: { message?: string } } | null)?.error?.message;
      throw new GraphError(
        detail ? `Graph ${res.status}: ${detail}` : `Graph ${res.status} on ${url}`,
        res.status,
        body,
      );
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  /** Follows @odata.nextLink. Ledger's largest list is ~2,000 rows, so paging
      to completion is correct here — do not add a page cap without a UI for it. */
  async listItems(listId: string, query = '$expand=fields&$top=200'): Promise<GraphListItem[]> {
    const out: GraphListItem[] = [];
    let url: string | undefined = `/sites/${this.siteId}/lists/${listId}/items?${query}`;
    while (url) {
      const page: GraphCollection<GraphListItem> = await this.request(url);
      out.push(...page.value);
      url = page['@odata.nextLink'];
    }
    return out;
  }

  /* Graph list-item $filter only works reliably on indexed columns, with
     `fields/ColumnName` syntax (§5). Non-indexed filters need a Prefer header
     that does exactly what its name suggests, so this method does not offer one:
     if a filter belongs here, the column belongs in the index definitions in
     provisioning/. Failing loudly beats failing randomly. */
  async filterItems(listId: string, filter: string, orderBy?: string): Promise<GraphListItem[]> {
    const parts = [`$expand=fields`, `$top=200`, `$filter=${encodeURIComponent(filter)}`];
    if (orderBy) parts.push(`$orderby=${encodeURIComponent(orderBy)}`);
    return this.listItems(listId, parts.join('&'));
  }

  async getItem(listId: string, itemId: string): Promise<GraphListItem> {
    return this.request(`/sites/${this.siteId}/lists/${listId}/items/${itemId}?$expand=fields`);
  }

  async createItem(listId: string, fields: ListItemFields): Promise<GraphListItem> {
    return this.request(`/sites/${this.siteId}/lists/${listId}/items`, {
      method: 'POST',
      body: JSON.stringify({ fields }),
    });
  }

  async updateItem(listId: string, itemId: string, fields: ListItemFields): Promise<ListItemFields> {
    return this.request(`/sites/${this.siteId}/lists/${listId}/items/${itemId}/fields`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    });
  }

  async deleteItem(listId: string, itemId: string): Promise<void> {
    await this.request(`/sites/${this.siteId}/lists/${listId}/items/${itemId}`, { method: 'DELETE' });
  }

  /* ─────────────────────────── Files ─────────────────────────── */

  /** Uploads a file into a document library and stamps its metadata.
   *
   *  Two requests minimum, and they cannot be one: a document library item is a
   *  file first and a list row second, so the bytes go to the drive and the
   *  columns are set on the list item the drive created.
   *
   *  Anything over 4MB goes through an upload session rather than a single PUT.
   *  Graph refuses a larger simple upload outright, and a comms room photo off a
   *  phone is regularly six or eight. */
  async uploadFile(
    listId: string,
    name: string,
    file: Blob,
    fields: ListItemFields,
  ): Promise<{ id: string; webUrl: string; fields: ListItemFields }> {
    const drive = await this.request<{ id: string }>(
      `/sites/${this.siteId}/lists/${listId}/drive?$select=id`,
    );

    /* conflictBehavior=rename, so filing a second "comms-room.jpg" produces
       "comms-room 1.jpg" rather than quietly replacing the first — a diagram
       nobody meant to overwrite is a diagram nobody can get back. */
    const item =
      file.size > SIMPLE_UPLOAD_MAX
        ? await this.uploadInChunks(drive.id, name, file)
        : await this.putBytes(
            `/drives/${drive.id}/root:/${encodeURIComponent(name)}:/content` +
              '?@microsoft.graph.conflictBehavior=rename',
            file,
          );

    /* The list item behind the file, which is where RecordId lives. Asked for
       separately because the upload response is a driveItem and carries none of
       the library's own columns. */
    const listItem = await this.request<{ id: string }>(
      `/drives/${drive.id}/items/${item.id}/listItem?$select=id`,
    );

    const written = await this.updateItem(listId, listItem.id, fields);
    return { id: listItem.id, webUrl: item.webUrl, fields: written };
  }

  /** A single PUT of the bytes. Not this.request — that sets a JSON content
   *  type on everything, and Graph takes the body literally. */
  private async putBytes(url: string, file: Blob): Promise<GraphDriveItem> {
    const token = await this.getToken(this.scopes);
    const res = await fetch(`${GRAPH}${url}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: file,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const detail = (body as { error?: { message?: string } } | null)?.error?.message;
      throw new GraphError(
        detail ? `Graph ${res.status}: ${detail}` : `Graph ${res.status} uploading ${url}`,
        res.status,
        body,
      );
    }
    return (await res.json()) as GraphDriveItem;
  }

  /** The resumable route, for anything Graph will not take in one go.
   *
   *  Chunks must be a multiple of 320 KiB and arrive in order; the session URL
   *  is pre-authorised, so the chunk PUTs deliberately carry no Authorization
   *  header — sending one is a documented way to get a 401 on a session that is
   *  working perfectly. */
  private async uploadInChunks(
    driveId: string,
    name: string,
    file: Blob,
  ): Promise<GraphDriveItem> {
    const session = await this.request<{ uploadUrl: string }>(
      `/drives/${driveId}/root:/${encodeURIComponent(name)}:/createUploadSession`,
      {
        method: 'POST',
        body: JSON.stringify({
          item: { '@microsoft.graph.conflictBehavior': 'rename', name },
        }),
      },
    );

    for (let start = 0; start < file.size; start += CHUNK) {
      const end = Math.min(start + CHUNK, file.size);
      const res = await fetch(session.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Range': `bytes ${start}-${end - 1}/${file.size}`,
        },
        body: file.slice(start, end),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const detail = (body as { error?: { message?: string } } | null)?.error?.message;
        throw new GraphError(
          detail ? `Graph ${res.status}: ${detail}` : `Graph ${res.status} uploading a chunk`,
          res.status,
          body,
        );
      }

      // The last chunk answers with the finished driveItem; the rest with 202.
      if (end >= file.size) return (await res.json()) as GraphDriveItem;
    }

    throw new Error('Upload finished without Graph returning the file.');
  }
}
